import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  HorizontalStack,
  Modal,
  Spinner,
  Stack,
  Text,
} from '@lego/klik-ui';
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiErrorDetail, useFetch } from '@/hooks/api';
import { useApplications } from '@/hooks/application';
import type { ApplicationVersionListResponse } from '@/hooks/application_version';
import { type CreateDeploymentRequest, useCreateDeployment } from '@/hooks/deployment';
import { useWorkspaceFiles } from '@/hooks/workspace/workspace_files_resource';
import { paths } from '@/utility/global_paths';

const selectProps = {
  as: 'select' as const,
  borderColor: 'gray.300',
  borderRadius: 'md',
  borderWidth: '1px',
  p: 2,
  width: '100%',
};

type Props = {
  workspaceId: number;
  fleetId: number;
  isOpen: boolean;
  onClose: () => void;
};

export const CreateDeploymentDialog = ({ workspaceId, fleetId, isOpen, onClose }: Props) => {
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | ''>('');
  const [selectedVersionId, setSelectedVersionId] = useState<number | 'null' | ''>('');
  const [selectedFileIds, setSelectedFileIds] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const navigate = useNavigate();

  const lastErrorDetail = useRef<string | undefined>(undefined);
  const onApiError = useCallback((err?: unknown) => {
    lastErrorDetail.current = getApiErrorDetail(err);
  }, []);
  const createDeployment = useCreateDeployment(workspaceId, fleetId, onApiError);

  const { data: applicationsData } = useApplications(workspaceId, { limit: 1000 });
  const applicationId = typeof selectedApplicationId === 'number' ? selectedApplicationId : null;
  const { data: versionsData, isLoading: isLoadingVersions } =
    useFetch<ApplicationVersionListResponse>(
      applicationId !== null
        ? `workspaces/${workspaceId}/applications/${applicationId}/versions`
        : null,
      (client) =>
        client.v2.workspaces[':workspaceId'].applications[':applicationId'].versions.$get({
          param: {
            workspaceId: workspaceId.toString(),
            applicationId: (applicationId as number).toString(),
          },
          query: { limit: '1000', sort: ['-id'] },
        })
    );
  const { data: filesData } = useWorkspaceFiles(workspaceId, { limit: 1000 });

  useEffect(() => {
    if (isOpen) {
      setSelectedApplicationId('');
      setSelectedVersionId('');
      setSelectedFileIds(new Set());
      setError(undefined);
    }
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset version when application changes
  useEffect(() => {
    setSelectedVersionId('');
  }, [selectedApplicationId]);

  const applications = applicationsData?.applications ?? [];
  const versions = selectedApplicationId ? (versionsData?.applicationVersions ?? []) : [];
  const files = filesData?.data?.files ?? [];

  const canSubmit = selectedVersionId !== '' && !isSubmitting;

  const handleFileToggle = (fileId: number) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(undefined);
    lastErrorDetail.current = undefined;
    try {
      const request: CreateDeploymentRequest = {
        applicationVersionId: selectedVersionId === 'null' ? null : selectedVersionId,
        fileIds: [...selectedFileIds],
      };

      const result = await createDeployment(request);
      onClose();
      navigate(paths.deploymentById(workspaceId, fleetId, result.id));
    } catch {
      setError(lastErrorDetail.current ?? 'Failed to create deployment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>Create Deployment</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <FormControl>
            <FormControl.Label>Application</FormControl.Label>
            <Box
              {...selectProps}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setSelectedApplicationId(e.target.value ? Number(e.target.value) : '')
              }
              value={selectedApplicationId}
            >
              <option value="">Select application</option>
              {applications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </Box>
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormControl.Label>Application Version</FormControl.Label>
            {selectedApplicationId && isLoadingVersions ? (
              <HorizontalStack alignItems="center" p={2} spacing={2}>
                <Spinner size="sm" />
                <Text color="gray.500" fontSize="sm">
                  Loading versions...
                </Text>
              </HorizontalStack>
            ) : (
              <Box
                {...selectProps}
                disabled={!selectedApplicationId}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const val = e.target.value;
                  setSelectedVersionId(val === 'null' ? 'null' : val ? Number(val) : '');
                }}
                value={selectedVersionId}
              >
                <option value="">Select version</option>
                <option value="null">None (empty deployment)</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version}
                    {v.tag ? ` (${v.tag})` : ''}
                  </option>
                ))}
              </Box>
            )}
          </FormControl>

          {files.length > 0 && (
            <FormControl mt={4}>
              <FormControl.Label>Files (optional)</FormControl.Label>
              <CheckboxGroup>
                <Stack maxH="200px" overflowY="auto" spacing={2}>
                  {files.map((file) => (
                    <Checkbox
                      isChecked={selectedFileIds.has(file.id)}
                      key={file.id}
                      onChange={() => handleFileToggle(file.id)}
                    >
                      {file.fileName}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </FormControl>
          )}

          {error && (
            <Text color="error.500" fontSize="sm" mt={4}>
              {error}
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <HorizontalStack spacing={2}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button isDisabled={!canSubmit} isLoading={isSubmitting} onClick={handleSubmit}>
              Deploy
            </Button>
          </HorizontalStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
